import * as RadixToast from '@radix-ui/react-toast';
import styled, { css } from 'styled-components';

import Button from 'components/ui/shared/Buttons';
import { Clickable } from 'components/ui/shared/Buttons/Clickable';

import { GLOBAL_NAV_WIDTH } from 'styles/layouts';
import {
  BLACK_ALPHA_002,
  BLUE_500,
  BORDER_BORDER_MUTED,
  BORDER_RADIUS_300,
  BOX_SHADOW_400,
  FONT_SIZE_14,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_REGULAR,
  HEIGHT_500,
  ICON_ICON_REVERSED,
  ICON_ICON_SUCCESS,
  LETTER_SPACING_DENSE,
  NEUTRAL_0,
  NEUTRAL_900,
  RED_600,
  SPACE_050,
  SPACE_150,
  SPACE_200,
  SPACE_250,
  SPACE_300,
  SPACE_400,
  TEXT_TEXT_PRIMARY,
  TEXT_TEXT_REVERSED,
  TEXT_TEXT_SECONDARY,
  WIDTH_500,
  Z_INDEX_5,
} from 'styles/tokens';
import { LINE_HEIGHT_REDUCED } from 'styles/typography';

import type { ToastPlacement, ToastVariant } from './Toast';

type VariantConfig = {
  /**
   * How Radix announces the variant: 'foreground' is an assertive live region, anything else polite.
   */
  announceType: 'background' | 'foreground';
  /**
   * Surface the whole card sits on, which is what carries the variant's tone.
   */
  background: string;
  /**
   * Color of the keyboard focus ring on the trailing controls. Per-variant because no one color clears
   * WCAG 1.4.11's 3:1 on surfaces this far apart — the house blue is 1.69:1 on the red card.
   */
  focusRingColor: string;
  /**
   * Color applied to the leading icon.
   */
  iconColor: string;
  /**
   * Color applied to the message, and to the title where `titleColor` is omitted.
   */
  textColor: string;
  /**
   * Overrides `textColor` for the title. Only the light surface needs it.
   */
  titleColor?: string;
};

/**
 * Shares its shape with {@link Alert}'s `TONE_CONFIG`, so a toast and a banner reporting the same
 * outcome are described the same way. Only a failure announces assertively — a stack can raise several
 * toasts in a row, and assertive ones would talk over each other.
 */
export const VARIANT_CONFIG: Record<ToastVariant, VariantConfig> = {
  error: {
    announceType: 'foreground',
    background: RED_600,
    focusRingColor: NEUTRAL_0,
    iconColor: ICON_ICON_REVERSED,
    textColor: TEXT_TEXT_REVERSED,
  },
  neutral: {
    announceType: 'background',
    background: NEUTRAL_900,
    focusRingColor: NEUTRAL_0,
    iconColor: ICON_ICON_REVERSED,
    textColor: TEXT_TEXT_REVERSED,
  },
  success: {
    announceType: 'background',
    background: NEUTRAL_0,
    focusRingColor: BLUE_500,
    iconColor: ICON_ICON_SUCCESS,
    textColor: TEXT_TEXT_SECONDARY,
    titleColor: TEXT_TEXT_PRIMARY,
  },
};

export type ToastPlacementProps = {
  /**
   * The placement whose axes drive the rule.
   */
  $placement: ToastPlacement;
};

export type ToastVariantProps = {
  /**
   * The variant whose surface and text colors drive the rule.
   */
  $variant: ToastVariant;
};

type ToastAlign = 'center' | 'left' | 'right';

type ToastEdge = 'bottom' | 'top';

type ToastPlacementAxes = {
  /**
   * Where along the edge the stack sits.
   */
  align: ToastAlign;
  /**
   * Which edge the stack is pinned to.
   */
  edge: ToastEdge;
};

export const PLACEMENT_AXES: Record<ToastPlacement, ToastPlacementAxes> = {
  'bottom-center': { align: 'center', edge: 'bottom' },
  'bottom-left': { align: 'left', edge: 'bottom' },
  'bottom-right': { align: 'right', edge: 'bottom' },
  'top-center': { align: 'center', edge: 'top' },
  'top-left': { align: 'left', edge: 'top' },
  'top-right': { align: 'right', edge: 'top' },
};

const TOAST_WIDTH = '400px';

const CONTENT_INSET = `calc(${GLOBAL_NAV_WIDTH} + ${SPACE_300})`;

type ToastAlignInsets = {
  /**
   * Inline start inset, or 'auto' where the stack is anchored from the far edge instead.
   */
  left: string;
  /**
   * Inline margins. Auto on both sides is what centres the stack.
   */
  margin: string;
  /**
   * Inline end inset, or 'auto' where the stack is anchored from the near edge instead.
   */
  right: string;
};

/**
 * Inline anchoring per alignment. 'center' pins `right` too, because auto margins only resolve equally
 * when both inline edges are pinned — a centred stack that loses it falls back to the left edge.
 */
const ALIGN_INSETS: Record<ToastAlign, ToastAlignInsets> = {
  center: { left: CONTENT_INSET, margin: '0 auto', right: SPACE_300 },
  left: { left: CONTENT_INSET, margin: '0', right: 'auto' },
  right: { left: 'auto', margin: '0', right: SPACE_300 },
};

const EDGE_LIFT: Record<ToastEdge, string> = {
  bottom: '-1',
  top: '1',
};

const isTopEdge = ({ $placement }: ToastPlacementProps) => PLACEMENT_AXES[$placement].edge === 'top';

const alignInsets = ({ $placement }: ToastPlacementProps) => ALIGN_INSETS[PLACEMENT_AXES[$placement].align];

const STACK_PEEK = SPACE_250;

const STACK_GAP = SPACE_150;

const STACK_SCALE_STEP = 0.05;

const TOAST_ENTER_MS = 180;

// Keep exit shorter than entrance so a dismissed toast leaves promptly.
const TOAST_EXIT_MS = 120;

const EXIT_LIFT = SPACE_200;

const STACK_EASING = 'cubic-bezier(0.2, 1, 0.4, 1)';

export const ToastViewport = styled(RadixToast.Viewport)<ToastPlacementProps>`
  --gap: ${STACK_GAP};
  --lift: ${({ $placement }) => EDGE_LIFT[PLACEMENT_AXES[$placement].edge]};
  --peek: ${STACK_PEEK};

  bottom: ${props => (isTopEdge(props) ? 'auto' : SPACE_300)};
  height: 0;
  left: ${props => alignInsets(props).left};
  list-style: none;
  margin: ${props => alignInsets(props).margin};

  /* Auto margins cannot shrink the card, so the clamp is what keeps a narrow window from overflowing. */
  max-width: calc(100vw - ${GLOBAL_NAV_WIDTH} - ${SPACE_300} * 2);
  outline: none;
  padding: 0;

  /* Only the cards themselves are hit targets, so the empty stack area stays click-through. */
  pointer-events: none;
  position: fixed;
  right: ${props => alignInsets(props).right};
  top: ${props => (isTopEdge(props) ? SPACE_300 : 'auto')};
  width: ${TOAST_WIDTH};
  z-index: ${Z_INDEX_5};
`;

export const ToastContent = styled.div`
  /* One centred row of four: status icon, message, action, dismiss. */
  align-items: center;

  /*
   * The depth cue sits on the measured element, not the root: under border-box a border on the root
   * falls outside the measurement and inside the clamp, leaving the card 2px short of its contents.
   */
  border: 1px solid transparent;
  border-radius: ${BORDER_RADIUS_300};
  column-gap: ${SPACE_300};
  display: flex;
  flex-direction: row;
  padding: ${SPACE_300} ${SPACE_300} ${SPACE_300} ${SPACE_400};
  transition: opacity ${TOAST_ENTER_MS}ms ${STACK_EASING};
`;

export const ToastRoot = styled(RadixToast.Root)<ToastPlacementProps & ToastVariantProps>`
  /*
   * Resting transform, in a custom property so the transform rule below can compose it with whatever
   * offset Radix writes mid-swipe. Substitution is at computed-value time, so --y still transitions.
   */
  --y: translateY(calc(var(--lift) * var(--peek) * var(--index))) scale(calc(1 - ${STACK_SCALE_STEP} * var(--index)));

  background: ${({ $variant }) => VARIANT_CONFIG[$variant].background};
  border-radius: ${BORDER_RADIUS_300};
  bottom: ${props => (isTopEdge(props) ? 'auto' : 0)};

  /*
   * The hairline ring is shadow-400's third layer, which the token omits. A shadow rather than a
   * border: it is paint-only, so it takes no layout space and cannot disturb the measured height.
   */
  box-shadow:
    ${BOX_SHADOW_400},
    inset 0 0 0 0.5px ${BLACK_ALPHA_002};

  /* Falls back to auto only until the first measurement lands; a card never transitions to or from it. */
  height: var(--own-height, auto);
  left: 0;

  /* Restores interactivity inside the click-through viewport. */
  pointer-events: auto;
  position: absolute;
  right: 0;
  top: ${props => (isTopEdge(props) ? 0 : 'auto')};

  /* Its own property, so a closing card can be nudged without restating its resting formula. */
  transform: var(--y) translateY(var(--exit-shift, 0)) translateY(var(--radix-toast-swipe-move-y, 0));
  transform-origin: ${props => (isTopEdge(props) ? 'top' : 'bottom')} center;
  transition:
    height ${TOAST_ENTER_MS}ms ${STACK_EASING},
    opacity ${TOAST_ENTER_MS}ms ${STACK_EASING},
    transform ${TOAST_ENTER_MS}ms ${STACK_EASING};
  z-index: var(--z-index);

  /*
   * The stack's ambient motion is decoration and goes; the swipe offset tracks the finger and stays.
   * Suppressed transitions fire no transitionend, so removal falls to the provider's fallback timer.
   * Reaches the resting states but not the exit, which restates the suppression in its own block.
   */
  @media (prefers-reduced-motion) {
    transition: none;

    ${ToastContent} {
      transition: none;
    }
  }

  /*
   * A toast arrives open, so there is no earlier frame to transition from: cards mount off-edge with
   * transitions suppressed and flip on the next frame, which is what plays the slide-in. Qualified by
   * the open state to outrank the resting rules below, which would otherwise win on source order.
   */
  &[data-mounted='false'][data-state='open'] {
    --y: translateY(calc(var(--lift) * -1 * (100% + ${SPACE_300})));

    opacity: 0;
    transition: none;
  }

  /* Behind the front card: clamped to the front card's height and muted to a depth cue. */
  &[data-expanded='false'][data-front='false'] {
    height: var(--front-toast-height, auto);
    overflow: hidden;

    /*
     * Not swipeable while stacked: --y scales below 1, which would attenuate the pixel delta Radix
     * derives from pointer coordinates. The front card covers the same area, so hover still works.
     */
    pointer-events: none;

    ${ToastContent} {
      border-color: ${BORDER_BORDER_MUTED};
      opacity: 0;
    }
  }

  &[data-expanded='true'] {
    --y: translateY(calc(var(--lift) * (var(--offset) + var(--gap) * var(--index))));

    height: var(--own-height, auto);

    /* Bridges the gap to the next card so travelling between them does not collapse the stack. */
    &::after {
      ${props =>
        isTopEdge(props)
          ? css`
              bottom: 100%;
            `
          : css`
              top: 100%;
            `}
      content: '';
      height: calc(var(--gap) + 1px);
      left: 0;
      position: absolute;
      right: 0;
    }
  }

  /*
   * Hidden rather than merely faded: Radix builds its F8 and Tab order from every mounted toast, and
   * focus() is a no-op on a hidden element — opacity alone would leave these in the keyboard cycle.
   */
  &[data-visible='false'] {
    opacity: 0;
    visibility: hidden;
  }

  /*
   * Last, so it wins over the resting states. The card has to *move* on close, not just fade: the
   * provider removes it when the transform transition ends, and an unchanged transform fires none.
   */
  &[data-state='closed'] {
    opacity: 0;

    /*
     * The card stays mounted for its slide-out and would keep taking clicks the whole way. A superseded
     * toast carries an action aimed at a state its subject has left, so the exit puts it out of reach.
     */
    pointer-events: none;
    transition:
      opacity ${TOAST_EXIT_MS}ms ease-in,
      transform ${TOAST_EXIT_MS}ms ease-in;

    /* Where the ambient suppression above cannot reach, for want of the specificity this block has. */
    @media (prefers-reduced-motion) {
      transition: none;
    }

    /* Sits at the anchor edge already, so clearing the viewport is a short trip. */
    &[data-front='true'] {
      --y: translateY(calc(var(--lift) * -1 * (100% + ${SPACE_300})));
    }

    /* The edge is a whole fan away, so it leaves from where it stands while the others close over it. */
    &[data-front='false'] {
      --exit-shift: calc(var(--lift) * -1 * ${EXIT_LIFT});
    }
  }

  /* On swipe end Radix swaps the move offset for an end offset; reading only the former snaps back. */
  &[data-swipe='end'] {
    transform: var(--y) translateY(var(--radix-toast-swipe-end-y, 0));
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  height: ${HEIGHT_500};
  width: ${WIDTH_500};
`;

export const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${SPACE_050};
  min-width: 0;
`;

const messageTextStyles = css`
  font-size: ${FONT_SIZE_14};
  letter-spacing: ${LETTER_SPACING_DENSE};
  line-height: ${LINE_HEIGHT_REDUCED};

  /* An entity's name can be one long unbreakable token, and the front card does not clip. */
  overflow-wrap: break-word;
`;

export const StyledTitle = styled(RadixToast.Title)<ToastVariantProps>`
  ${messageTextStyles}
  color: ${({ $variant }) => VARIANT_CONFIG[$variant].titleColor ?? VARIANT_CONFIG[$variant].textColor};
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;

export const StyledDescription = styled(RadixToast.Description)<ToastVariantProps>`
  ${messageTextStyles}
  color: ${({ $variant }) => VARIANT_CONFIG[$variant].textColor};
  font-weight: ${FONT_WEIGHT_REGULAR};
`;

// Matches {@link Alert}'s dismiss button: a bare glyph the size of the icon it holds, with no fill of its own.
export const CloseButton = styled(Clickable)<ToastVariantProps>`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: ${HEIGHT_500};
  justify-content: center;
  width: ${WIDTH_500};

  /* Matches Button's focus ring, since this one sits on surfaces the browser default is not tuned for. */
  &:focus-visible {
    outline: ${SPACE_050} solid ${({ $variant }) => VARIANT_CONFIG[$variant].focusRingColor};
    outline-offset: ${SPACE_050};
  }
`;

export const ActionButton = styled(Button).attrs({
  size: 'x-small' as const,
  variant: 'secondary' as const,
})<ToastVariantProps>`
  flex-shrink: 0;

  /* Overrides Button's fixed blue ring. The offset puts it on the card, so the surface decides it. */
  &:focus-visible {
    outline-color: ${({ $variant }) => VARIANT_CONFIG[$variant].focusRingColor};
  }
`;
